<template>
  <div class="w-full bg-slate-50 min-h-screen p-6" style="font-family: 'Inter', sans-serif;">
    <div class="max-w-5xl mx-auto">
      
      <!-- HEADER -->
      <div class="mb-6 flex items-center justify-between">
        <div>
          <button @click="$router.back()" class="flex items-center gap-2 text-slate-500 hover:text-orange-600 transition-colors mb-2">
            <v-icon small color="inherit">mdi-arrow-left</v-icon>
            <span class="text-[10px] font-bold uppercase tracking-wider">Voltar</span>
          </button>
          <h1 class="text-2xl font-bold text-slate-700">{{ id ? 'Editar Licitação' : 'Cadastrar Licitação' }}</h1>
        </div>
      </div>

      <div class="space-y-6">
        <!-- TABS SECTION -->
        <div v-if="id" class="mb-6">
          <v-tabs v-model="tab" grow class="custom-tabs rounded-xl overflow-hidden shadow-sm">
            <v-tab :key="0"><v-icon small class="mr-2">mdi-file-document-edit</v-icon> Dados</v-tab>
            <v-tab :key="1"><v-icon small class="mr-2">mdi-cash-multiple</v-icon> Dotação</v-tab>
            <v-tab :key="2"><v-icon small class="mr-2">mdi-trophy-outline</v-icon> Propostas</v-tab>
            <v-tab :key="3"><v-icon small class="mr-2">mdi-handshake-outline</v-icon> Contratos</v-tab>
          </v-tabs>
        </div>

        <v-tabs-items v-model="tab" class="bg-transparent">
          <!-- TAB 0: DADOS DA LICITAÇÃO -->
          <v-tab-item :key="0">
            <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
              <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
                <v-icon color="orange" small>mdi-file-document-edit</v-icon>
                <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">DADOS DA LICITAÇÃO</h2>
              </div>
              
              <div class="p-8">
                <v-form ref="form" v-model="valid" lazy-validation>
                  <div class="grid grid-cols-1 md:grid-cols-12 gap-6">
                    <!-- Prefeitura -->
                    <div class="md:col-span-12">
                      <label class="block text-[10px] font-bold uppercase text-slate-500 mb-1">Prefeitura</label>
                      <v-autocomplete
                        :items="prefectures"
                        item-value="prefecture_id"
                        item-text="prefecture_social_name"
                        v-model="data.prefecture_id"
                        :rules="fieldRules"
                        solo flat dense outlined
                        class="custom-field"
                        placeholder="Selecione a Prefeitura..."
                      />
                    </div>

                    <!-- Numero do Processo -->
                    <div class="md:col-span-6">
                      <label class="block text-[10px] font-bold uppercase text-slate-500 mb-1">Número do Processo</label>
                      <v-text-field
                        v-model="data.bidding_process_number"
                        solo flat dense outlined
                        class="custom-field"
                        placeholder="Ex: 001/2024"
                      />
                    </div>

                    <!-- Tipo de Licitação -->
                    <div class="md:col-span-6">
                      <label class="block text-[10px] font-bold uppercase text-slate-500 mb-1">Tipo de Licitação</label>
                      <v-autocomplete
                        :items="types"
                        item-value="type_id"
                        item-text="type_desc"
                        v-model="data.type_id"
                        :rules="fieldRules"
                        solo flat dense outlined
                        class="custom-field"
                        placeholder="Selecione o Tipo..."
                      />
                    </div>

                    <!-- Modalidade -->
                    <div class="md:col-span-6">
                      <label class="block text-[10px] font-bold uppercase text-slate-500 mb-1">Modalidade</label>
                      <v-autocomplete
                        :items="modalities"
                        item-value="modality_id"
                        item-text="modality_desc"
                        v-model="data.modality_id"
                        :rules="fieldRules"
                        solo flat dense outlined
                        class="custom-field"
                        placeholder="Selecione a Modalidade..."
                      />
                    </div>

                    <!-- Publicação do Edital -->
                    <div class="md:col-span-6">
                      <label class="block text-[10px] font-bold uppercase text-slate-500 mb-1">Publicação do Edital</label>
                      <v-text-field
                        v-model="data.bidding_publication_notice"
                        v-mask-date.br
                        solo flat dense outlined
                        class="custom-field"
                        placeholder="DD/MM/AAAA"
                      />
                    </div>

                    <!-- Habilitação -->
                    <div class="md:col-span-4">
                      <label class="block text-[10px] font-bold uppercase text-slate-500 mb-1">Habilitação</label>
                      <v-text-field
                        v-model="data.bidding_qualification"
                        v-mask-date.br
                        solo flat dense outlined
                        class="custom-field"
                        placeholder="DD/MM/AAAA"
                      />
                    </div>

                    <!-- Julgamento -->
                    <div class="md:col-span-4">
                      <label class="block text-[10px] font-bold uppercase text-slate-500 mb-1">Julgamento Propostas</label>
                      <v-text-field
                        v-model="data.bidding_tenders"
                        v-mask-date.br
                        solo flat dense outlined
                        class="custom-field"
                        placeholder="DD/MM/AAAA"
                      />
                    </div>

                    <!-- Homologação -->
                    <div class="md:col-span-4">
                      <label class="block text-[10px] font-bold uppercase text-slate-500 mb-1">Homologação</label>
                      <v-text-field
                        v-model="data.bidding_homologation"
                        v-mask-date.br
                        solo flat dense outlined
                        class="custom-field"
                        placeholder="DD/MM/AAAA"
                      />
                    </div>

                    <!-- Valor -->
                    <div class="md:col-span-4">
                      <label class="block text-[10px] font-bold uppercase text-slate-500 mb-1">Valor Total (R$)</label>
                      <v-text-field
                        v-model="data.bidding_value"
                        v-mask-decimal.br="2"
                        solo flat dense outlined
                        class="custom-field"
                        placeholder="0,00"
                        prepend-inner-icon="mdi-currency-usd"
                      />
                    </div>

                    <!-- Numero Propostas -->
                    <div class="md:col-span-4">
                      <label class="block text-[10px] font-bold uppercase text-slate-500 mb-1">Nº de Propostas</label>
                      <v-text-field
                        v-model="data.bidding_number_of_proposals"
                        v-mask-number
                        solo flat dense outlined
                        class="custom-field"
                        placeholder="0"
                      />
                    </div>

                    <!-- Status Ativo -->
                    <div class="md:col-span-4 flex items-end pb-2">
                       <label class="flex items-center gap-3 cursor-pointer select-none">
                          <input 
                            type="checkbox" 
                            v-model="data.bidding_status"
                            class="w-5 h-5 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                          >
                          <span class="text-[10px] font-bold uppercase text-slate-500">Licitação Ativa</span>
                       </label>
                    </div>

                    <!-- Objeto -->
                    <div class="md:col-span-12">
                      <label class="block text-[10px] font-bold uppercase text-slate-500 mb-1">Objeto</label>
                      <v-textarea
                        v-model="data.bidding_object"
                        solo flat dense outlined
                        class="custom-field"
                        placeholder="Descrição detalhada do objeto da licitação..."
                        rows="4"
                      />
                    </div>
                  </div>
                </v-form>
              </div>

              <!-- FOOTER DENTRO DO CARD -->
              <div class="bg-slate-50 px-8 py-5 border-t border-slate-200 flex justify-end items-center gap-4">
                <button 
                  @click="$router.push('/biddings-manager')"
                  class="text-slate-500 hover:text-slate-700 font-bold text-[11px] uppercase px-4 py-2 transition-colors flex-none"
                >
                  Cancelar
                </button>
                <div class="flex-none">
                  <button
                    @click="validate"
                    :disabled="loader"
                    class="bg-orange-500 hover:bg-orange-600 text-white font-bold text-[11px] px-6 py-2.5 rounded shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all w-auto max-w-max whitespace-nowrap flex items-center gap-2"
                  >
                    <v-icon small color="white" class="mr-1" style="font-size: 14px;">mdi-check</v-icon>
                    <span v-if="loader" class="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></span>
                    <span v-else>CONFIRMAR DADOS</span>
                  </button>
                </div>
              </div>
            </section>
          </v-tab-item>

          <!-- OUTRAS ABAS (COMPONENTES EXTERNOS) -->
          <v-tab-item :key="1" class="mt-2">
            <Donations :bidding_id="id" :prefecture_id="data.prefecture_id" />
          </v-tab-item>
          <v-tab-item :key="2" class="mt-2">
            <Items :bidding_id="id" />
          </v-tab-item>
          <v-tab-item :key="3" class="mt-2">
            <Contracts :bidding_id="id" />
          </v-tab-item>
        </v-tabs-items>
      </div>
    </div>

    <!-- Snackbar -->
    <v-snackbar
      multi-line
      :color="snack.color"
      v-model="snack.show"
      transition="fade-transition"
    >
      {{ snack.message }}
      <template v-slot:action="{ attrs }">
        <v-btn dark text v-bind="attrs" @click="snack.show = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import Donations from "./donnations";
import Items from "./items";
import Contracts from "./contracts";
export default {
  props: ["id"],
  components: {
    Donations,
    Items,
    Contracts,
  },
  data: () => ({
    tab: 0,
    valid: false,
    loader: false,
    prefectures: [],
    modalities: [],
    types: [],
    fieldRules: [(v) => !!v || "Este campo é requerido"],
    snack: {
      show: false,
      message: "",
      color: "success",
    },
    data: {
      modality_id: null,
      prefecture_id: null,
      type_id: null,
      bidding_id: null,
      bidding_process_number: "",
      bidding_publication_notice: "",
      bidding_qualification: "",
      bidding_tenders: "",
      bidding_homologation: "",
      bidding_value: 0,
      bidding_number_of_proposals: 0,
      bidding_object: "",
      bidding_status: false,
    },
  }),
  created() {
    this.$http.get("/prefectures").then((r) => (this.prefectures = r.data));
    this.$http
      .get("/biddings/modalities")
      .then((r) => (this.modalities = r.data));
    this.$http.get("/biddings/types").then((r) => (this.types = r.data));
    if (this.id) {
      this.$store.dispatch("setLoader", true);
      this.$http.get(`biddings/${this.id}`).then((r) => {
        this.data = r.data[0];
        
        if (this.data.bidding_homologation) this.data.bidding_homologation = this.convertDate(this.data.bidding_homologation);
        if (this.data.bidding_tenders) this.data.bidding_tenders = this.convertDate(this.data.bidding_tenders);
        if (this.data.bidding_qualification) this.data.bidding_qualification = this.convertDate(this.data.bidding_qualification);
        if (this.data.bidding_publication_notice) this.data.bidding_publication_notice = this.convertDate(this.data.bidding_publication_notice);
        
        if (this.data.bidding_value !== null && this.data.bidding_value !== undefined && this.data.bidding_value !== '') {
          this.data.bidding_value = Number(this.data.bidding_value).toFixed(2);
        }

        delete this.data.created_at;
        this.$store.dispatch("setLoader", false);
      });
    }
  },
  methods: {
    validate() {
      if (this.$refs.form.validate()) {
        this.createOrUpdate();
      } else {
        this.snack.show = true;
        this.snack.color = "warning";
        this.snack.message = "Atenção: um ou mais campos precisam ser preenchidos!";
        this.loader = false;
      }
    },
    convertDate(v) {
      if (v) {
        v = v.split('T')[0]; // Handle ISO date strings if present
        v = v.replace(/-/g, "");
        let year = v.substring(0, 4);
        let month = v.substring(4, 6);
        let day = v.substring(6, 8);
        return `${day}/${month}/${year}`;
      } else {
        return "";
      }
    },
    createOrUpdate() {
      this.loader = true;
      const method = this.id ? 'put' : 'post';
      const url = this.id ? `/biddings/${this.id}` : '/biddings';
      
      this.$store.dispatch("setLoader", this.loader);
      this.$http[method](url, this.data)
        .then((r) => {
          this.snack.show = true;
          this.snack.message = r.data.result || "Sucesso!";
          this.snack.color = "success";
          this.loader = false;
          this.$store.dispatch("setLoader", false);
          setTimeout(() => {
            this.$router.push("/biddings-manager");
          }, 1500);
        })
        .catch((e) => {
          this.snack.show = true;
          this.snack.message = e.message;
          this.snack.color = "error";
          this.loader = false;
          this.$store.dispatch("setLoader", false);
        });
    },
  },
};
</script>

<style scoped>
/* Utility classes mimicking Tailwind */
.min-h-screen { min-height: 100vh; }
.bg-slate-50 { background-color: #f8fafc; }
.bg-slate-100 { background-color: #f1f5f9; }
.bg-white { background-color: #ffffff; }
.p-6 { padding: 1.5rem; }
.p-8 { padding: 2rem; }
.px-6 { padding-left: 1.5rem; padding-right: 1.5rem; }
.px-8 { padding-left: 2rem; padding-right: 2rem; }
.py-4 { padding-top: 1rem; padding-bottom: 1rem; }
.py-5 { padding-top: 1.25rem; padding-bottom: 1.25rem; }
.mb-1 { margin-bottom: 0.25rem; }
.mb-2 { margin-bottom: 0.5rem; }
.mb-6 { margin-bottom: 1.5rem; }
.mt-2 { margin-top: 0.5rem; }
.gap-2 { gap: 0.5rem; }
.gap-3 { gap: 0.75rem; }
.gap-4 { gap: 1rem; }
.gap-6 { gap: 1.5rem; }

.rounded-xl { border-radius: 0.75rem; }
.rounded-lg { border-radius: 0.5rem; }
.shadow-sm { box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05); }
.overflow-hidden { overflow: hidden; }
.border { border-style: solid; border-width: 1px; }
.border-slate-200 { border-color: #e2e8f0; }

.text-2xl { font-size: 1.5rem; line-height: 2rem; }
.text-slate-700 { color: #334155; }
.text-slate-500 { color: #64748b; }
.text-white { color: #ffffff; }
.font-bold { font-weight: 700; }
.uppercase { text-transform: uppercase; }
.tracking-wider { letter-spacing: 0.05em; }
.tracking-widest { letter-spacing: 0.1em; }

/* Custom Overrides */
.custom-tabs ::v-deep .v-tabs-bar {
  background-color: #333333 !important;
  height: 48px;
}
.custom-tabs ::v-deep .v-tab {
  color: rgba(255, 255, 255, 0.6) !important;
  font-size: 10px !important;
  font-weight: 700 !important;
  letter-spacing: 0.1em !important;
  text-transform: uppercase !important;
}
.custom-tabs ::v-deep .v-tab--active {
  color: #ffffff !important;
  background-color: rgba(255, 255, 255, 0.05);
}
.custom-tabs ::v-deep .v-tabs-slider {
  background-color: #f97316 !important;
}

.custom-field ::v-deep .v-input__slot {
  background-color: #f8fafc !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: none !important;
  border-radius: 0.5rem !important;
  min-height: 44px !important;
}
.custom-field ::v-deep .v-input__slot:focus-within {
  border-color: #f97316 !important;
  box-shadow: 0 0 0 1px #f97316 !important;
}
.custom-field ::v-deep .v-label {
  font-size: 0.825rem !important;
  color: #94a3b8 !important;
}
.custom-field ::v-deep input, .custom-field ::v-deep textarea {
  font-size: 0.875rem !important;
  color: #334155 !important;
}
</style>

<script>
import Donations from "./donnations";
import Items from "./items";
import Contracts from "./contracts";
export default {
  props: ["id"],
  components: {
    Donations,
    Items,
    Contracts,
  },
  data: () => ({
    tab: 0,
    valid: false,
    loader: false,
    prefectures: [],
    modalities: [],
    types: [],
    fieldRules: [(v) => !!v || "Este campo é requerido"],
    snack: {
      show: false,
      message: "",
      color: "success",
    },
    data: {
      modality_id: null,
      prefecture_id: null,
      type_id: null,
      bidding_id: null,
      bidding_process_number: "",
      bidding_publication_notice: "",
      bidding_qualification: "",
      bidding_tenders: "",
      bidding_homologation: "",
      bidding_value: 0,
      bidding_number_of_proposals: 0,
      bidding_object: "",
      bidding_status: false,
    },
  }),
  created() {
    this.$http.get("/prefectures").then((r) => (this.prefectures = r.data));
    this.$http
      .get("/biddings/modalities")
      .then((r) => (this.modalities = r.data));
    this.$http.get("/biddings/types").then((r) => (this.types = r.data));
    if (this.id) {
      this.$store.dispatch("setLoader", true);
      this.$http.get(`biddings/${this.id}`).then((r) => {
        this.data = r.data[0];
        this.data.bidding_homologation = this.convertDate(
          this.data.bidding_homologation
        );
        this.data.bidding_tenders = this.convertDate(this.data.bidding_tenders);
        this.data.bidding_qualification = this.convertDate(
          this.data.bidding_qualification
        );
        this.data.bidding_publication_notice = this.convertDate(
          this.data.bidding_publication_notice
        );
        if (this.data.bidding_value !== null && this.data.bidding_value !== undefined && this.data.bidding_value !== '') {
          this.data.bidding_value = Number(this.data.bidding_value).toFixed(2);
        }

        delete this.data.created_at;
        this.$store.dispatch("setLoader", false);
      });
    }
  },
  methods: {
    validate() {
      this.snack.show = true;
      this.loader = true;
      if (this.$refs.form.validate()) {
        this.createOrUpdate();
      } else {
        this.snack.show = true;
        this.snack.color = "warning";
        this.snack.message =
          "Atencão um ou mais campos precisam ser preenchidos!";
        this.loader = false;
      }
    },
    convertDate(v) {
      if (v) {
        v = v.replace("-", "").replace("-", "");
        let year = v.substring(0, 4);
        let month = v.substring(4, 6);
        let day = v.substring(6, 8);
        return `${day}/${month}/${year}`;
      } else {
        return false;
      }
    },
    createOrUpdate() {
      this.loader = true;
      if (this.id) {
        this.$store.dispatch("setLoader", this.loader);
        this.$http
          .put(`/biddings/${this.id}`, this.data)
          .then((r) => {
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.loader = true;
            setTimeout(() => {
              this.$router.push("/biddings-manager");
            }, 1500);
            this.$store.dispatch("setLoader", this.loader);
          })
          .catch((e) => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.loader = false;
            this.$store.dispatch("setLoader", false);
          });
      } else {
        this.$store.dispatch("setLoader", this.loader);
        this.$http
          .post("/biddings", this.data)
          .then((r) => {
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.snack.color = "success";
            this.loader = false;
            this.$store.dispatch("setLoader", this.loader);
            setTimeout(() => {
              this.$router.push("/biddings-manager");
            }, 1500);
          })
          .catch((e) => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.loader = false;
            this.$store.dispatch("setLoader", false);
          });
      }
    },
  },
};
</script>

<style>
.title {
  text-transform: uppercase;
  font-weight: 400;
}

.flexd {
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
}
.flexd p {
  margin: 10px 0;
  font-weight: bold;
  text-transform: uppercase;
}
</style>
